/**
 * Venue edit modal body – location, courts, admins, booking access, delete.
 * Used inside VenueManagement edit modal.
 */

import React from 'react';
import { EntityProfileDisplay } from './EntityProfileFields';
import PlaceAutocomplete, { isPlacesAvailable } from './PlaceAutocomplete';
import { GAMES } from '../constants/games';

export default function VenueEditModalContent({
  venue,
  venueById,
  canEditVenueProperties,
  managerEmails,
  venueAdminEmailToAdd,
  setVenueAdminEmailToAdd,
  groups,
  gamesForSelect,
  saving,
  editingVenueId,
  editingCourtKey,
  courtForm,
  setCourtForm,
  courtFormError,
  setCourtFormError,
  editingLocationVenueId,
  editingLocationFormattedAddress,
  setEditingLocationFormattedAddress,
  editingLocationLatitude,
  setEditingLocationLatitude,
  editingLocationLongitude,
  setEditingLocationLongitude,
  editingLocationPlaceId,
  setEditingLocationPlaceId,
  editingLocationPhone,
  setEditingLocationPhone,
  editingLocationWebsite,
  setEditingLocationWebsite,
  editingLocationMapUrl,
  setEditingLocationMapUrl,
  enhancingLocation,
  setEnhancingLocation,
  editingPrivateVenueId,
  editingPrivateIsPrivate,
  setEditingPrivateIsPrivate,
  editingPrivateAllowedGroupIds,
  setEditingPrivateAllowedGroupIds,
  startEditVenueLocation,
  saveVenueLocation,
  cancelEditVenueLocation,
  startEditCourt,
  startAddCourt,
  saveCourtForm,
  cancelCourtForm,
  handleDeleteCourt,
  updateCourtFormSlot,
  addAvailabilitySlotToForm,
  removeAvailabilitySlotFromForm,
  startEditPrivateVenue,
  saveVenuePrivateSettings,
  cancelEditPrivateVenue,
  handleAddVenueAdmin,
  handleRemoveVenueAdmin,
  handleDeleteVenue,
  getPlaceDetails,
  buildMapUrlFromPlaceDetails,
  DAY_NAMES,
  formatSlot,
  formatVenueAddress,
}) {
  if (!venue) return null;

  return (
    <div className="venue-edit-modal-content">
      <p className="venue-address">
        {formatVenueAddress(venue)}
        {venue.mapUrl && (
          <a href={venue.mapUrl} target="_blank" rel="noopener noreferrer" className="venue-map-link"> · View on map</a>
        )}
      </p>
      {canEditVenueProperties(venue) && (
        <div className="venue-location-section venue-card-location">
          <strong>Enhance with Google Maps</strong>
          <p className="venue-location-hint">
            {isPlacesAvailable()
              ? 'Search to fill address, phone, website and map link from Google.'
              : 'Add REACT_APP_GOOGLE_MAPS_API_KEY to .env and restart the app to see Google search suggestions.'}
          </p>
          {editingLocationVenueId === venue.id ? (
            <div className="venue-location-edit">
              <PlaceAutocomplete
                value={editingLocationFormattedAddress}
                onChange={setEditingLocationFormattedAddress}
                onSelect={async (place) => {
                  if (place.formattedAddress) setEditingLocationFormattedAddress(place.formattedAddress);
                  setEditingLocationLatitude(place.latitude != null ? String(place.latitude) : '');
                  setEditingLocationLongitude(place.longitude != null ? String(place.longitude) : '');
                  if (place.placeId) setEditingLocationPlaceId(place.placeId);
                  if (place.placeId) {
                    setEnhancingLocation(true);
                    try {
                      const details = await getPlaceDetails(place.placeId);
                      if (details) {
                        if (details.formattedAddress) setEditingLocationFormattedAddress(details.formattedAddress);
                        if (details.latitude != null) setEditingLocationLatitude(String(details.latitude));
                        if (details.longitude != null) setEditingLocationLongitude(String(details.longitude));
                        if (details.formattedPhoneNumber) setEditingLocationPhone(details.formattedPhoneNumber);
                        if (details.website) setEditingLocationWebsite(details.website);
                        const mapUrl = buildMapUrlFromPlaceDetails(details);
                        if (mapUrl) setEditingLocationMapUrl(mapUrl);
                      }
                    } finally {
                      setEnhancingLocation(false);
                    }
                  }
                }}
                placeholder="Search address or place…"
                inputStyle={{ maxWidth: '100%', marginBottom: '6px' }}
              />
              {enhancingLocation && <p className="venue-location-fetching">Fetching details from Google…</p>}
              <div className="venue-location-coords">
                <input
                  type="text"
                  value={editingLocationLatitude}
                  onChange={(e) => setEditingLocationLatitude(e.target.value)}
                  placeholder="Latitude"
                  style={{ width: '100px' }}
                />
                <input
                  type="text"
                  value={editingLocationLongitude}
                  onChange={(e) => setEditingLocationLongitude(e.target.value)}
                  placeholder="Longitude"
                  style={{ width: '100px' }}
                />
              </div>
              <div className="venue-location-details-row">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editingLocationPhone}
                  onChange={(e) => setEditingLocationPhone(e.target.value)}
                  placeholder="From Google or manual"
                  style={{ maxWidth: '100%' }}
                />
              </div>
              <div className="venue-location-details-row">
                <label>Website</label>
                <input
                  type="url"
                  value={editingLocationWebsite}
                  onChange={(e) => setEditingLocationWebsite(e.target.value)}
                  placeholder="From Google or manual"
                  style={{ maxWidth: '100%' }}
                />
              </div>
              <div className="venue-location-details-row">
                <label>Map URL</label>
                <input
                  type="url"
                  value={editingLocationMapUrl}
                  onChange={(e) => setEditingLocationMapUrl(e.target.value)}
                  placeholder="Google Maps link"
                  style={{ maxWidth: '100%' }}
                />
              </div>
              <div className="court-form-actions" style={{ marginTop: '8px' }}>
                <button type="button" className="btn-primary" onClick={() => saveVenueLocation(venue.id)} disabled={saving}>
                  {saving ? 'Saving…' : 'Save location & details'}
                </button>
                <button type="button" className="btn-secondary" onClick={cancelEditVenueLocation}>Cancel</button>
              </div>
            </div>
          ) : (
            <p className="venue-location-display">
              {(venue.formattedAddress || venue.address || '—').trim() || '—'}
              {(venue.latitude != null && venue.longitude != null) && (
                <span className="venue-location-coords-display"> ({venue.latitude}, {venue.longitude})</span>
              )}
              {(venue.phone || venue.website) && (
                <span className="venue-location-extra"> · {[venue.phone, venue.website].filter(Boolean).join(' · ')}</span>
              )}
              <button type="button" className="btn-secondary btn-sm" onClick={() => startEditVenueLocation(venue)} style={{ marginLeft: '8px' }}>
                Enhance with Google Maps
              </button>
            </p>
          )}
        </div>
      )}
      <EntityProfileDisplay entity={venue} showAvatar={false} />
      <div className="venue-games">
        {(venue.gameIds || ['table-tennis']).map((gid) => {
          const g = GAMES.find((x) => x.id === gid);
          return g ? <span key={gid} className="venue-game-badge">{g.icon} {g.name}</span> : null;
        })}
      </div>
      {canEditVenueProperties(venue) && (
        <div className="venue-admins-section">
          <strong>Venue admins</strong>
          <ul className="venue-admins-list">
            {(venue.managerIds || []).map((uid) => (
              <li key={uid}>
                <span>{managerEmails[uid] || uid}</span>
                {(venue.managerIds || []).length > 1 && (
                  <button type="button" className="btn-destructive btn-sm" onClick={() => handleRemoveVenueAdmin(venue.id, uid)}>Remove</button>
                )}
              </li>
            ))}
          </ul>
          <div className="add-venue-admin-inline">
            <input
              type="email"
              placeholder="Admin email"
              value={venueAdminEmailToAdd[venue.id] || ''}
              onChange={(e) => setVenueAdminEmailToAdd((p) => ({ ...p, [venue.id]: e.target.value }))}
            />
            <button type="button" className="btn-primary" onClick={() => handleAddVenueAdmin(venue.id)} disabled={saving}>Add</button>
          </div>
        </div>
      )}
      {canEditVenueProperties(venue) && (
        <div className="venue-private-section">
          <strong>Booking access</strong>
          {editingPrivateVenueId === venue.id ? (
            <div className="venue-private-edit">
              <label className="chip-label">
                <input
                  type="checkbox"
                  checked={editingPrivateIsPrivate}
                  onChange={(e) => setEditingPrivateIsPrivate(e.target.checked)}
                />
                <span>Private venue – only selected groups can book</span>
              </label>
              {editingPrivateIsPrivate && (
                <div className="venue-private-groups">
                  <span className="venue-private-groups-label">Allowed groups:</span>
                  {groups.map((g) => (
                    <label key={g.id} className="chip-label">
                      <input
                        type="checkbox"
                        checked={editingPrivateAllowedGroupIds.includes(g.id)}
                        onChange={(e) => {
                          if (e.target.checked) setEditingPrivateAllowedGroupIds((prev) => [...prev, g.id]);
                          else setEditingPrivateAllowedGroupIds((prev) => prev.filter((id) => id !== g.id));
                        }}
                      />
                      <span>{g.name || g.id}</span>
                    </label>
                  ))}
                  {groups.length === 0 && <span className="venue-private-no-groups">No groups. Create groups first.</span>}
                </div>
              )}
              <div className="court-form-actions">
                <button type="button" className="btn-primary" onClick={() => saveVenuePrivateSettings(venue.id)} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn-secondary" onClick={cancelEditPrivateVenue}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="venue-private-display">
              <p className="venue-private-summary">
                {venue.isPrivate
                  ? `Private – only members of ${(venue.allowedGroupIds || []).length} group(s) can book.`
                  : 'Public – any user can book.'}
              </p>
              <button type="button" className="btn-secondary btn-sm" onClick={() => startEditPrivateVenue(venue)}>
                Edit booking access
              </button>
            </div>
          )}
        </div>
      )}
      {canEditVenueProperties(venue) && (
        <button
          type="button"
          className="btn-destructive btn-sm"
          onClick={() => handleDeleteVenue(venue.id)}
        >
          Delete venue
        </button>
      )}
      <div className="venue-courts">
        <strong>Courts / grounds</strong>
        {(venue.courts || []).length === 0 && editingVenueId !== venue.id && (
          <p className="venue-no-courts">No courts added.</p>
        )}

        {editingVenueId === venue.id && courtForm && (
          <div className="court-edit-form card-ui">
            <h4>{editingCourtKey === 'new' ? 'New court' : 'Edit court'}</h4>
            <div className="form-row">
              <label>Court name</label>
              <input
                type="text"
                value={courtForm.name}
                onChange={(e) => { setCourtForm((p) => ({ ...p, name: e.target.value })); setCourtFormError(''); }}
                placeholder="e.g. Court 1"
                aria-invalid={!!courtFormError}
                aria-describedby={courtFormError ? 'court-form-error' : undefined}
              />
              {courtFormError && (
                <p id="court-form-error" className="venue-form-error" role="alert">{courtFormError}</p>
              )}
            </div>
            <div className="form-row">
              <label>Sports</label>
              <div className="venue-game-chips">
                {gamesForSelect.map((g) => (
                  <label key={g.id} className="chip-label">
                    <input
                      type="checkbox"
                      checked={(courtForm.gameIds || []).includes(g.id)}
                      onChange={(e) => {
                        if (e.target.checked) setCourtForm((p) => ({ ...p, gameIds: [...(p.gameIds || []), g.id] }));
                        else setCourtForm((p) => ({ ...p, gameIds: (p.gameIds || []).filter((id) => id !== g.id) }));
                      }}
                    />
                    <span>{g.icon} {g.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-row">
              <label>Pricing (by venue admin)</label>
              <div className="form-row-inline" style={{ flexWrap: 'wrap', gap: '12px' }}>
                <label className="chip-label">
                  <input
                    type="radio"
                    name="priceType"
                    checked={(courtForm.priceType || 'per_hour') === 'per_hour'}
                    onChange={() => setCourtForm((p) => ({ ...p, priceType: 'per_hour', priceUnit: 'per hour' }))}
                  />
                  <span>Per hour</span>
                </label>
                <label className="chip-label">
                  <input
                    type="radio"
                    name="priceType"
                    checked={(courtForm.priceType || 'per_match') === 'per_match'}
                    onChange={() => setCourtForm((p) => ({ ...p, priceType: 'per_match', priceUnit: 'per match' }))}
                  />
                  <span>Per match</span>
                </label>
              </div>
              <div className="form-row-inline" style={{ marginTop: '8px' }}>
                <input
                  type="text"
                  value={courtForm.price}
                  onChange={(e) => setCourtForm((p) => ({ ...p, price: e.target.value }))}
                  placeholder="e.g. 200"
                  style={{ width: '100px' }}
                />
                <span className="court-price-unit">{courtForm.priceUnit || 'per hour'}</span>
              </div>
            </div>
            <div className="form-row">
              <label>Availability (weekly slots)</label>
              {(courtForm.availabilitySlots || []).map((slot, idx) => (
                <div key={idx} className="slot-row">
                  <select
                    value={slot.dayOfWeek}
                    onChange={(e) => updateCourtFormSlot(idx, 'dayOfWeek', parseInt(e.target.value, 10))}
                  >
                    {DAY_NAMES.map((d, i) => (
                      <option key={i} value={i + 1}>{d}</option>
                    ))}
                  </select>
                  <input
                    type="time"
                    value={slot.startTime || ''}
                    onChange={(e) => updateCourtFormSlot(idx, 'startTime', e.target.value)}
                  />
                  <span>–</span>
                  <input
                    type="time"
                    value={slot.endTime || ''}
                    onChange={(e) => updateCourtFormSlot(idx, 'endTime', e.target.value)}
                  />
                  <button type="button" className="btn-destructive btn-sm" onClick={() => removeAvailabilitySlotFromForm(idx)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn-secondary btn-sm" onClick={addAvailabilitySlotToForm}>
                + Add slot
              </button>
            </div>
            <div className="court-form-actions">
              <button type="button" className="btn-primary" onClick={saveCourtForm} disabled={saving}>
                {saving ? 'Saving…' : 'Save court'}
              </button>
              <button type="button" className="btn-secondary" onClick={cancelCourtForm}>Cancel</button>
              {editingCourtKey !== 'new' && (
                <button type="button" className="btn-destructive btn-sm" onClick={() => handleDeleteCourt(venue.id, courtForm.id)}>
                  Delete court
                </button>
              )}
            </div>
          </div>
        )}

        {(venue.courts || []).map((c) => (
          <div key={c.id} className="court-row">
            <div className="court-info">
              <span className="court-name">{c.name}</span>
              <span className="court-sports">
                {(c.gameIds || []).map((gid) => {
                  const g = GAMES.find((x) => x.id === gid);
                  return g ? <span key={gid} className="venue-game-badge">{g.icon} {g.name}</span> : null;
                })}
              </span>
              {c.price != null && c.price !== '' && (
                <span className="court-price">₹{c.price} {(c.priceType === 'per_match' ? 'per match' : (c.priceUnit || 'per hour'))}</span>
              )}
              {(c.availabilitySlots || []).length > 0 && (
                <span className="court-slots">
                  {(c.availabilitySlots || []).map((s, i) => (
                    <span key={i} className="slot-badge">{formatSlot(s)}</span>
                  ))}
                </span>
              )}
            </div>
            {canEditVenueProperties(venue) && editingVenueId !== venue.id && (
              <button type="button" className="btn-secondary btn-sm" onClick={() => startEditCourt(venue, c)}>Edit</button>
            )}
          </div>
        ))}

        {canEditVenueProperties(venue) && editingVenueId !== venue.id && (
          <button type="button" className="btn-secondary btn-sm" onClick={() => startAddCourt(venue)}>
            + Add court
          </button>
        )}
      </div>
    </div>
  );
}
